import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function RochdaleCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Rochdale Pothole Claims | RED Best Practice Rating | Fixtyer"
        description="Rochdale Borough Council earns RED for best practice despite GREEN spend. 72% of reactive budget goes on potholes, 12% of U-roads are in RED condition, and the council carried out zero preventative maintenance in 2022/23. See what the DfT AMBER rating means for your claim."
        canonical="/councils/rochdale"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Rochdale', url: '/councils/rochdale' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Rochdale spends above its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Rochdale\'s capital spend exceeds its allocation, but the overall rating is AMBER and best practice is RED. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. With 71.77% of reactive budget going directly to pothole intervention and only 17.04% of capital on preventative works in 2024/25, the council\'s own figures show a reactive-heavy programme.',
          },
          {
            question: 'What does the RED best practice rating mean for my claim?',
            answer: 'The DfT has formally scored Rochdale\'s asset management approach as failing national best-practice standards. The council\'s own report admits zero preventative maintenance in 2022/23, incomplete pothole data that year due to a system change, and that inventory and condition data will take "the next few years" to build. That undermines a Section 58 defence based on a mature, data-led maintenance system.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 576.93km — 79% of Rochdale\'s 732.27km carriageway network. At the latest survey, 12% of U-roads were in RED condition, down from 17% in 2020 but still representing roughly 69km of residential streets, estate roads and minor routes. The council surveys U-roads annually via its Annual Engineering Inspection programme, but AEI may show higher poor-condition rates than SCANNER because it evaluates entire road sections rather than 10-metre sub-sections.',
          },
          {
            question: 'Does the drop in U-road RED from 17% to 12% weaken my claim?',
            answer: 'Not necessarily — 12% RED still means roughly one in eight U-roads should be considered for maintenance. The council also notes that AEI\'s holistic, engineering-led evaluation "may indicate a higher proportion of the network in poor condition compared to SCANNER." A declining percentage does not prove your specific defect was inspected or repaired within a reasonable timeframe.',
          },
          {
            question: 'Does the 2022/23 pothole data gap matter?',
            answer: 'Yes. Rochdale recorded only 3,768 pothole repairs in 2022/23 — less than half the surrounding years — and its own footnote states this is "part data due to change of works ordering system." That same year, the council carried out zero preventative maintenance after bringing highways delivery back in-house. If your incident falls in or around that period, the council\'s own records are incomplete.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Rochdale Borough Council"
        councilSlug="rochdale"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/rochdale"
        headline="Rochdale Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Rochdale</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Rochdale: RED Best Practice, 71.77% of Reactive Spend on Potholes
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Rochdale Borough Council spends above its DfT capital allocation and earns a GREEN spend scorecard.
            Yet the DfT rates its overall performance <strong className="text-amber-400">AMBER</strong> and its
            asset management approach <strong className="text-rose-400">RED</strong> — because the council's own
            transparency report reveals <strong className="text-rose-400">71.77% of reactive spend went on pothole
            intervention</strong> in 2024/25, zero preventative maintenance in 2022/23, and a highways data system
            still being built "over the next few years."
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">71.77%</div>
            <div className="text-white font-medium mt-2">Of reactive spend on potholes (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Against 17.04% of capital on preventative works. The council filled an estimated 8,890 potholes
              in 2024/25 alone — roughly 24 every day across its 732km network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The RED Best Practice Rating */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            The DfT's RED best-practice scorecard is the strongest claim-relevant signal in Rochdale's
            ratings — and the council's own report explains why
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We did not carry out any preventative maintenance in 2022/23 due to the highway service delivery returning in-house and had insufficient timescales to arrange a preventative treatment programme"
              source="Rochdale Borough Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="We will continue to build up our information data in relation to inventory & condition over the next few years to drive a data lead prioritisation of maintenance works across different assets."
              source="Rochdale Borough Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What RED Best Practice Means</h3>
              <p className="text-rose-800 text-sm mb-3">
                The DfT incentive fund scores best practice on asset management maturity — inventory
                completeness, preventative-first spending, reliable performance data, and alignment with
                Well-Managed Highway Infrastructure. A RED score means Rochdale falls short of national
                standards on these measures, regardless of how much it spends.
              </p>
              <p className="text-rose-800 text-sm">
                For Section 58, a council claiming a "reasonable system" of maintenance needs documented
                processes, consistent data, and planned intervention — not a programme that is still
                transitioning systems and building its asset inventory.
              </p>
            </div>

            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Alloy System Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Rochdale recently purchased the Alloy works ordering and asset management system. It uses
                this to log assets, create works orders, and link to its CRM — but the council explicitly
                states it will take years to build complete inventory and condition data.
              </p>
              <p className="text-surface-600 text-sm">
                Pothole figures for 2022/23 are flagged as partial data due to the system change. A
                council still assembling its asset management foundation is a weaker Section 58 defendant
                than one with mature, continuous records.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Preventative vs Reactive Split</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">0.00%</div>
                <div className="text-brand-300 text-sm">Preventative maintenance (2022/23)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">17.04%</div>
                <div className="text-brand-300 text-sm">Preventative share of capital (2024/25)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">65.19%</div>
                <div className="text-brand-300 text-sm">Average reactive share of revenue (5 years)</div>
              </div>
            </div>
            <p className="text-brand-200">
              An average of <strong className="text-white">65.19% of revenue spend</strong> over the last
              five years has gone on reactive maintenance — primarily pothole repairs and urgent defect
              fixes. The council aims to shift towards planned preventative work, but its own projected
              2025/26 split is still 21.23% preventative (capital) against 70.00% reactive (revenue).
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and AEI survey data from Rochdale's own transparency report — AMBER
            overall because local roads are not holding steady
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (101.33km — 13.8% of network)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">4%</td>
                    <td className="px-4 py-2 text-center font-bold">21%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road condition is broadly stable, with a spike to 6% RED in 2023. Surveys use a mixture
              of SCANNER and AEI data on an annual basis.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (60.86km — 8.3% of network)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">25%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">68%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5%</td>
                    <td className="px-4 py-2 text-center font-bold">24%</td>
                    <td className="px-4 py-2 text-center font-bold">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads hit their worst point in 2023: 7% RED and only 68% GREEN — a third of the classified
              local road network needing or approaching maintenance. The 2024 figure improved to 5% RED, but
              nearly a third of B/C roads remain in amber condition.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (576.93km — 79% of network)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-rose-900">Year</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-rose-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 text-sm mb-4">
              At 12% RED in 2024, roughly <strong>69km of Rochdale's residential and minor roads</strong> should
              be considered for maintenance. U-road condition is collected via the Annual Engineering Inspection
              programme, surveying 100% of the network each year.
            </p>
            <QuoteCard
              quote="While AEI may indicate a higher proportion of the network in poor condition compared to SCANNER, this reflects its holistic, engineering-led evaluation of entire maintenance lengths."
              source="Rochdale Borough Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Spending context */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend, Reactive Reality
          </h2>
          <p className="text-surface-600 mb-8">
            Rochdale's capital spend exceeds its DfT allocation — but the money is not buying a
            preventative-first programme
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£4,265k</td>
                  <td className="px-4 py-3 text-center">£7,611k</td>
                  <td className="px-4 py-3 text-center">£1,853k</td>
                  <td className="px-4 py-3 text-center">41.56%</td>
                  <td className="px-4 py-3 text-center">56.56%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£4,121k</td>
                  <td className="px-4 py-3 text-center">£5,106k</td>
                  <td className="px-4 py-3 text-center">£2,000k</td>
                  <td className="px-4 py-3 text-center">14.59%</td>
                  <td className="px-4 py-3 text-center">59.78%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£3,710k</td>
                  <td className="px-4 py-3 text-center">£2,840k</td>
                  <td className="px-4 py-3 text-center">£2,077k</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">0.00%</td>
                  <td className="px-4 py-3 text-center">60.95%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£4,641k</td>
                  <td className="px-4 py-3 text-center">£7,734k</td>
                  <td className="px-4 py-3 text-center">£2,245k</td>
                  <td className="px-4 py-3 text-center">12.92%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">76.89%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£5,098k</td>
                  <td className="px-4 py-3 text-center">£5,781k</td>
                  <td className="px-4 py-3 text-center">£2,782k</td>
                  <td className="px-4 py-3 text-center">17.04%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">71.77%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£4,710k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£5,781k</td>
                  <td className="px-4 py-3 text-center">£2,782k</td>
                  <td className="px-4 py-3 text-center">21.23%</td>
                  <td className="px-4 py-3 text-center">70.00%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Why Spend Is GREEN</h3>
              <p className="text-emerald-800 text-sm">
                Rochdale consistently spends more capital than the DfT allocates — £5,781k against £4,710k
                projected for 2025/26, and £7,734k against £4,641k in 2023/24. Over five years, 49km of
                carriageway received preventative maintenance and 6.5km of footways were improved.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Why That Does Not Fix Your Road</h3>
              <p className="text-rose-800 text-sm">
                Preventative and reactive percentages are funded from separate budgets and do not add to 100%.
                In 2024/25, <strong>71.77% of reactive spend went directly on pothole intervention</strong> —
                patching symptoms, not preventing them. The council itself says maintenance is "not solely
                determined by the result of these surveys" but by "local needs and priorities."
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The maintenance of roads is not solely determined by the result of these surveys. Other factors, based on local needs and priorities, will determine where maintenance is carried out to ensure the safety and longevity of the entire road network through a risk-based, highway asset management approach."
            source="Rochdale Borough Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole conveyor */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            37,675 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway resurfaced (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway preserved (km)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">8,247</td>
                  <td className="px-6 py-4 text-center">5.038</td>
                  <td className="px-6 py-4 text-center">22.604</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">8,679</td>
                  <td className="px-6 py-4 text-center">5.622</td>
                  <td className="px-6 py-4 text-center">7.666</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">3,768*</td>
                  <td className="px-6 py-4 text-center">4.994</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0.00</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">8,091</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">12.515</td>
                  <td className="px-6 py-4 text-center">7.643</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,890</td>
                  <td className="px-6 py-4 text-center">6.196</td>
                  <td className="px-6 py-4 text-center">11.082</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">37,675</td>
                  <td className="px-6 py-4 text-center font-bold">34.365</td>
                  <td className="px-6 py-4 text-center font-bold">48.995</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~21 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Rochdale fills roughly 21 potholes per day. The revenue programme also carries out approximately 5,500 carriageway
                repairs and 2,000 footway repairs annually, plus 45,905 gully cleans. That is a network
                producing defects faster than planned works can prevent them.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The 2022/23 Asterisk</h3>
              <p className="text-amber-800 text-sm">
                The council's own footnote: "part data due to change of works ordering system." That same
                year saw zero kilometres of carriageway preservation and 0.00% preventative maintenance spend.
                If your incident falls in this period, Rochdale's records are explicitly incomplete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            What Rochdale says it does to know the condition of its roads — and where the gaps are
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">How Roads Are Surveyed</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A and B/C roads:</strong> annual SCANNER and AEI surveys</li>
                <li>• <strong>U-roads:</strong> Annual Engineering Inspection — 100% surveyed each year</li>
                <li>• <strong>Highway safety inspections:</strong> undertaken against prescribed timescales per the council's highway safety policy</li>
                <li>• <strong>Structures:</strong> general inspection once every two years</li>
                <li>• <strong>Skid resistance:</strong> annual survey to inform the capital programme</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reported Defect Response</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reported defects are prioritised for repair within <strong>20 working days</strong> or logged
                for future consideration. The council's CRM system captures customer enquiries with dashboard
                reporting, and inspection teams log defects into Alloy with photographic evidence.
              </p>
              <p className="text-amber-800 text-sm">
                But the council also states road maintenance is not solely survey-driven — "other factors,
                based on local needs and priorities" determine where work happens. That discretion is exactly
                what prior reports and photos can challenge under Section 58.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Rochdale must maintain its roads. Under Section 58,
              it can defend a claim only by proving a <strong className="text-white">reasonable system</strong> of
              inspection and repair. For your specific defect, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 12% of U-roads the council's own survey rated RED?</li>
              <li>• If a safety inspection or CRM report flagged the defect, was it repaired within 20 working days?</li>
              <li>• Does the RED best-practice rating undermine the council's claim to a mature asset management system?</li>
              <li>• If your incident was in 2022/23, can the council produce complete pothole and inspection records?</li>
              <li>• With 71.77% of reactive spend on potholes, was your defect one of thousands the system could not prevent?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits its data is still being built, carried out zero preventative maintenance
              in 2022/23, and spends nearly three-quarters of its reactive budget patching potholes has a
              harder Section 58 argument than its GREEN spend scorecard suggests.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER Council With GREEN Spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Rochdale is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital expenditure exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-roads surveyed annually via AEI — 100% coverage each year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition broadly stable — 75% GREEN in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented highway safety inspection policy and 20-working-day defect response target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 49km of preventative carriageway treatment over five years
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on well-surveyed A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What Works In Yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — DfT confirms asset management falls short
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 71.77% of reactive spend on pothole intervention in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 0.00% preventative maintenance in 2022/23 — admitted in the council's own words
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12% of U-roads in RED — roughly 69km of the network needing maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 37,675 potholes filled in five years — defects forming faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Inventory and condition data still being built — incomplete 2022/23 records
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but RED best practice, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, CRM enquiries) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, cite the 12% RED figure and whether AEI flagged your section</li>
              <li>• Timing — incidents near 2022/23 can exploit the admitted data gap and zero preventative year</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Rochdale's own transparency data — including the RED best-practice rating — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Rochdale?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            GREEN spend and RED best practice demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED best-practice argument</li>
                <li>• No prior-report search</li>
                <li>• No 2022/23 data-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED best-practice rating cited</li>
                <li>✅ 71.77% reactive pothole spend documented</li>
                <li>✅ U-road RED condition data argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Rochdale</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=rochdale"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Rochdale Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Rochdale spends above its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Rochdale's capital spend exceeds its allocation,
                but the overall rating is AMBER and best practice is RED. Section 58 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend.
                With 71.77% of reactive budget going directly to pothole intervention and only 17.04% of capital
                on preventative works in 2024/25, the council's own figures show a reactive-heavy programme.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the RED best practice rating mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT has formally scored Rochdale's asset management approach as failing national
                best-practice standards. The council's own report admits zero preventative maintenance in
                2022/23, incomplete pothole data that year due to a system change, and that inventory and
                condition data will take "the next few years" to build. That undermines a Section 58 defence
                based on a mature, data-led maintenance system.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 576.93km — 79% of Rochdale's 732.27km carriageway network. At the latest
                survey, 12% of U-roads were in RED condition, down from 17% in 2020 but still representing
                roughly 69km of residential streets, estate roads and minor routes. The council surveys
                U-roads annually via its Annual Engineering Inspection programme, but AEI may show higher
                poor-condition rates than SCANNER because it evaluates entire road sections rather than
                10-metre sub-sections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the drop in U-road RED from 17% to 12% weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — 12% RED still means roughly one in eight U-roads should be considered for
                maintenance. The council also notes that AEI's holistic, engineering-led evaluation "may indicate
                a higher proportion of the network in poor condition compared to SCANNER." A declining percentage
                does not prove your specific defect was inspected or repaired within a reasonable timeframe.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2022/23 pothole data gap matter?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Rochdale recorded only 3,768 pothole repairs in 2022/23 — less than half the surrounding
                years — and its own footnote states this is "part data due to change of works ordering system."
                That same year, the council carried out zero preventative maintenance after bringing highways
                delivery back in-house. If your incident falls in or around that period, the council's own records
                are incomplete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.rochdale.gov.uk/downloads/file/2902/department-for-transport-incentive-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Rochdale Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
